import React from 'react';
import { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './SignAndSubmit.scss';
import { Tooltip, Checkbox, Button, DriftIcons } from 'drift-ui';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { sendDOFirebaseEvent } from 'utils/utils';
import { AnalyticsEvents, AnalyticsPages, SigningState } from 'client-experience-toolbox';
import { RootState } from 'app/configureStore';
import SignAndSubmitIcon from '../../assets/images/sign_and_submit_icon.svg';
import InfoIcon from '../../assets/images/info.svg';
import { useTranslation } from 'react-i18next';

export const SignAndSubmit: FC = () => {
  const dispatch = useDispatch();
  const [signingState, setSigningState] = useState(SigningState.Disabled);
  const [creditTermsChecked, setCreditTermsChecked] = useState(false);
  const [perjuryTermsChecked, setPerjuryTermsChecked] = useState(false);
  const application = useSelector((state: RootState) => state?.applicationReducer?.factoringApplication);
  const { ArrowBack, CheckCircleIcon } = DriftIcons;
  const { t } = useTranslation('common', { keyPrefix: 'sign_and_submit_section' });

  const applicant = application?.applicationData?.applicants?.[0];

  useEffect(() => {
    if (creditTermsChecked && perjuryTermsChecked) {
      setSigningState(SigningState.Incomplete);
    } else {
      setSigningState(SigningState.Disabled);
    }
  }, [creditTermsChecked, perjuryTermsChecked]);

  let statusColor;
  let displayedIcon;
  switch (signingState) {
    case SigningState.Incomplete:
      statusColor = '#FFF0C7';
      displayedIcon = <ArrowBack className={styles.arrowIcon} htmlColor={'#ef3824'} />;
      break;
    case SigningState.Done:
      statusColor = '#FFF0C7';
      displayedIcon = <CheckCircleIcon color="success" />;
      break;
    case SigningState.Disabled:
    default:
      statusColor = '#F9F9F9';
      displayedIcon = <ArrowBack className={styles.arrowIcon} htmlColor={'#999999'} />;
      break;
  }

  const handleSignatureClick = async () => {
    handleClickEvent('Additional Signer Sign field');
    if (signingState === SigningState.Incomplete) {
      setSigningState(SigningState.Done);
    }
  };

  const handleClickEvent = (description: string) => {
    return sendDOFirebaseEvent(AnalyticsEvents.CLICK, AnalyticsPages.FACTORING_APPLICATION_FORM, {
      description: description,
      applicationType: application?.type ?? 'unknown',
      applicationId: application?._id ?? 'unknown',
      user_email: applicant?.email ?? 'unknown',
    });
  };

  const handleSubmit = async () => {
    await handleClickEvent('Additional Signer Submit');

    dispatch({ type: 'SET_SUBMISSION_STATUS', submissionStatus: 'submitting' });
  };

  return (
    <div className={styles.container}>
      <div className={styles.borderBox}>
        <img src={SignAndSubmitIcon} alt="Expired Icon" className={styles.signAndSubmitIcon} width={235} height={235} />
        <div className={styles.textContainer}>
          <p className={styles.titleText}>{t('title')}</p>
          <p className={styles.descriptionText}>{t('description')}</p>
        </div>
      </div>
      <div className={styles.checkboxSection}>
        <label className={cx(styles.checkboxAndText, styles.creditTermTextBox)}>
          <Checkbox
            id="credit-checkbox"
            className={styles.checkbox}
            onChange={async (event) => {
              setCreditTermsChecked(event.target.checked);
              handleClickEvent('Additional Signer Verification and Credit Authorization');
            }}
          />
          <p className={styles.checkboxText}>
            {t('credit_terms')}
            <Link to="../terms_and_conditions" className={styles.linkText}>
              {t('credit_terms_link')}
            </Link>
          </p>
        </label>
        <label className={styles.checkboxAndText}>
          <Checkbox
            id="security-checkbox"
            className={styles.checkbox}
            onChange={async (event) => {
              setPerjuryTermsChecked(event.target.checked);
              handleClickEvent('Additional Signer Security Agreement and Filing Authorization');
            }}
          />
          <p className={styles.checkboxText}>
            {t('perjury_terms')}
            <Link to="../w9_certification" className={styles.linkText}>
              {t('perjury_terms_link')}
            </Link>
          </p>
        </label>
      </div>
      <div className={styles.signatureContainer}>
        <button
          className={cx(
            styles.signatureButton,
            signingState === SigningState.Incomplete && styles.signatureContainerIncomplete,
          )}
          onClick={handleSignatureClick}
          disabled={signingState === SigningState.Disabled}
        >
          <div className={styles.signatureTextBox}>
            <p className={cx(signingState === SigningState.Done && styles.deliusText, styles.signatureText)}>
              {signingState === SigningState.Done ? `${applicant?.firstName} ${applicant?.lastName}` : t('tap_to_sign')}
            </p>
            <div className={styles.signatureStatus} style={{ backgroundColor: statusColor }}>
              {displayedIcon}
              {signingState !== SigningState.Done && (
                <p
                  className={cx(
                    signingState === SigningState.Incomplete && styles.signatureButtonText,
                    styles.signatureText,
                  )}
                >
                  {t('sign_here')}
                </p>
              )}
            </div>
          </div>
        </button>
        <Tooltip
          title={t('tooltip')}
          placement="top"
          enterTouchDelay={0}
          leaveTouchDelay={8000}
          slotProps={{
            tooltip: {
              className: styles.tooltip,
            },
          }}
        >
          <img
            src={InfoIcon}
            alt="Information Icon"
            width={24}
            height={24}
            className={styles.tooltipIcon}
            tabIndex={0}
          />
        </Tooltip>
      </div>
      <div className={styles.footer}>
        <Button
          variant="contained"
          size="large"
          className={styles.submitButton}
          disabled={signingState !== SigningState.Done}
          onClick={handleSubmit}
        >
          {t('submit')}
        </Button>
      </div>
    </div>
  );
};

export default SignAndSubmit;
